import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, LinearProgress, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslate } from "react-admin";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useLearningCatalog, useLearningCoursePayload, useMyLearning } from "../../../hooks/useLearningCatalog";
import {
  createLearningCommandId,
  StartedLearningCourse,
  useLearningCommands,
} from "../../../hooks/useLearningCommands";
import { parseLearningCoursePayload } from "../../../lib/learning/contentContract";
import { space } from "../../../theme/designTokens";
import { BlockRenderer } from "./BlockRenderer";

type StartStatus = "idle" | "starting" | "ready" | "error";

export const LessonPlayer = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const catalog = useLearningCatalog();
  const courseSummary = catalog.courses.find(course => course.id === courseId);
  const learning = useMyLearning();
  const courseState = learning.courses.find(course => course.courseId === courseId);
  const activeVersion =
    courseState?.status === "IN_PROGRESS" && courseState.startedVersions.length > 0
      ? courseState.startedVersions[courseState.startedVersions.length - 1]
      : courseSummary?.version;
  const payload = useLearningCoursePayload(courseSummary?.id, learning.loading ? undefined : activeVersion);
  const { startCourse, completeLesson, completing } = useLearningCommands();
  const [blockIndex, setBlockIndex] = useState(0);
  const [startStatus, setStartStatus] = useState<StartStatus>("idle");
  const [startAttempt, setStartAttempt] = useState(0);
  const [commandError, setCommandError] = useState<string | null>(null);
  const startInputs = useRef(new Map<string, Parameters<typeof startCourse>[0]>());
  const startRequests = useRef(new Map<string, Promise<StartedLearningCourse>>());
  const completionInputs = useRef(new Map<string, Parameters<typeof completeLesson>[0]>());

  const course = payload.course;
  const lesson = course?.lessons.find(item => item.id === lessonId);
  const lessonIndex = course?.lessons.findIndex(item => item.id === lessonId) ?? -1;
  const currentBlock = lesson?.blocks[blockIndex];
  const firstIncompleteLessonIndex =
    course?.lessons.findIndex(item => !courseState?.completedLessonIds.includes(item.id)) ?? -1;
  const lessonAllowed =
    lessonIndex >= 0 &&
    Boolean(
      courseState?.completedLessonIds.includes(lessonId ?? "") ||
        firstIncompleteLessonIndex === -1 ||
        lessonIndex <= firstIncompleteLessonIndex
    );

  useEffect(() => {
    setBlockIndex(0);
    setCommandError(null);
  }, [courseId, lessonId]);

  useEffect(() => {
    if (!courseSummary || !course || !lesson || !lessonAllowed || courseSummary.locked) {
      return undefined;
    }
    const key = `${course.id}:${course.version}`;
    let input = startInputs.current.get(key);
    if (!input) {
      input = {
        clientEventId: createLearningCommandId(),
        courseId: course.id,
        courseVersion: course.version,
        startedAt: new Date().toISOString(),
      };
      startInputs.current.set(key, input);
    }
    let request = startRequests.current.get(key);
    if (!request) {
      request = startCourse(input);
      startRequests.current.set(key, request);
    }
    let active = true;
    setStartStatus("starting");
    void request
      .then(result => {
        if (!active) return;
        const startedPayload = parseLearningCoursePayload(result.payload);
        if (startedPayload.id !== course.id || startedPayload.version !== course.version) {
          throw new Error("Started learning payload identity mismatch");
        }
        setStartStatus("ready");
      })
      .catch(() => {
        if (!active) return;
        startRequests.current.delete(key);
        setStartStatus("error");
      });
    return () => {
      active = false;
    };
  }, [course, courseSummary, lesson, lessonAllowed, startAttempt, startCourse]);

  if ((catalog.loading && !courseSummary) || learning.loading || (courseSummary && payload.loading && !course)) {
    return (
      <Box display="grid" minHeight={320} sx={{ placeItems: "center" }}>
        <CircularProgress aria-label={translate("learning.player.loading")} />
      </Box>
    );
  }
  if (catalog.error || learning.error || payload.error) {
    return <Alert severity="error">{translate("learning.course.contentError")}</Alert>;
  }
  if (!courseSummary || !courseState || courseSummary.locked || !course || !lesson || !lessonAllowed || !currentBlock) {
    return <Alert severity="warning">{translate("learning.player.notAvailable")}</Alert>;
  }
  if (startStatus === "starting" || startStatus === "idle") {
    return (
      <Box display="grid" minHeight={320} sx={{ placeItems: "center" }}>
        <CircularProgress aria-label={translate("learning.player.starting")} />
      </Box>
    );
  }
  if (startStatus === "error") {
    return (
      <Alert
        severity="error"
        action={
          <Button
            onClick={() => {
              setCommandError(null);
              setStartAttempt(attempt => attempt + 1);
            }}
          >
            {translate("learning.retry")}
          </Button>
        }
      >
        {translate("learning.player.startError")}
      </Alert>
    );
  }

  const finishLesson = async (): Promise<void> => {
    const key = `${course.id}:${course.version}:${lesson.id}`;
    let input = completionInputs.current.get(key);
    if (!input) {
      input = {
        clientEventId: createLearningCommandId(),
        courseId: course.id,
        courseVersion: course.version,
        lessonId: lesson.id,
        completedAt: new Date().toISOString(),
      };
      completionInputs.current.set(key, input);
    }
    setCommandError(null);
    try {
      await completeLesson(input);
      const nextLesson = course.lessons[lessonIndex + 1];
      navigate(
        nextLesson ? `/learning/courses/${course.id}/lessons/${nextLesson.id}` : `/learning/courses/${course.id}/quiz`
      );
    } catch {
      setCommandError(translate("learning.player.completeError"));
    }
  };

  const isLastBlock = blockIndex === lesson.blocks.length - 1;

  return (
    <Stack spacing={space.xl}>
      <Button
        component={Link}
        to={`/learning/courses/${course.id}`}
        startIcon={<ArrowBack />}
        sx={{ alignSelf: "flex-start" }}
      >
        {translate("learning.player.backToCourse")}
      </Button>
      <Box>
        <Typography color="text.secondary">{course.title}</Typography>
        <Typography component="h1" variant="h4" gutterBottom>
          {lesson.title}
        </Typography>
        <Typography color="text.secondary">
          {translate("learning.player.blockProgress", { current: blockIndex + 1, total: lesson.blocks.length })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={((blockIndex + 1) / lesson.blocks.length) * 100}
          sx={{ mt: space.md }}
        />
      </Box>
      {commandError && <Alert severity="error">{commandError}</Alert>}
      <BlockRenderer key={currentBlock.id} block={currentBlock} />
      <Stack direction="row" justifyContent="space-between" gap={space.md}>
        <Button
          disabled={blockIndex === 0 || completing}
          startIcon={<ArrowBack />}
          onClick={() => setBlockIndex(index => Math.max(0, index - 1))}
        >
          {translate("learning.player.previous")}
        </Button>
        {isLastBlock ? (
          <Button
            data-testid="learning-complete-lesson"
            variant="contained"
            disabled={completing}
            onClick={() => void finishLesson()}
          >
            {translate("learning.player.completeLesson")}
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={() => setBlockIndex(index => Math.min(lesson.blocks.length - 1, index + 1))}
          >
            {translate("learning.player.next")}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
